import type { MapDispatchToProps } from 'react-redux';
import { connect } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { addonTabCreatedThunk } from 'state/addonForm/addonForm.thunks';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { ApplyStatus, BcsKey, ProductTradingOption, ProductTradingOptions } from 'state/tradeAllocationForm';
import {
  tradeAllocationFightingBackCommentChanged,
  tradeAllocationFightingBackJustificationChanged,
  tradeAllocationFormResetRequested,
} from 'state/tradeAllocationForm/tradeAllocationForm.actions';
import * as tradeAllocationSelector from 'state/tradeAllocationForm/tradeAllocationForm.selector';
import {
  openInSecondaryMarketFromTradeAllocationThunk,
  tradeAllocationApplyRequestedThunk,
} from 'state/tradeAllocationForm/tradeAllocationForm.thunks';
import { isTradeAllocationApplicableForUser } from 'state/user';

interface OwnProps {
  tabId: string;
}
interface StateProps {
  fightingBackJustification?: string;
  fightingBackComment?: string;
  isReadyToApply: boolean;
  tradeId: string;
  applyStatus: ApplyStatus;
  isReadOnly: boolean;
  isLoading: boolean;
  canApply: boolean;
  isin: string;
  productTradingFormOptions?: ProductTradingOptions;
  bcsKey?: BcsKey;
  productId: string;
  rfqId: string;
  isTradeWithMonetaryScheduleNotStarted: boolean;
  isTradeWithMonetaryScheduleClosed: boolean;
  isMonetarySchedulePriceUnavailable: boolean;
}

interface DispatchProps {
  onFightingBackJustificationChanged: (newJustification: string) => void;
  onFightingBackCommentChanged: (newComment: string) => void;
  onApply: () => void;
  onReload: (tradeId: string) => void;
  openInSecondaryMarketQuote: (formOption: ProductTradingOption, isin: string, bcsKey?: BcsKey) => void;
  openInAddOn: (productId: string, quoteId: string, isin: string) => void;
}
export type SidePanelProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps = (state: AppState, { tabId }: OwnProps): StateProps => {
  const hasApplyRights = isTradeAllocationApplicableForUser(state);

  return {
    fightingBackJustification: tradeAllocationSelector.getFightingBackJustification(state, tabId),
    fightingBackComment: tradeAllocationSelector.getFightingBackComment(state, tabId),
    isReadyToApply: tradeAllocationSelector.isReadyToApplyTradeAllocations(state, tabId),
    isTradeWithMonetaryScheduleNotStarted: tradeAllocationSelector.isPrecomPeriodNotStarted(state, tabId),
    isTradeWithMonetaryScheduleClosed: tradeAllocationSelector.isPrecomPeriodClosed(state, tabId),
    isMonetarySchedulePriceUnavailable:
      tradeAllocationSelector.isPrecomPeriodOpen(state, tabId) &&
      !tradeAllocationSelector.getMonetarySchedule(state, tabId)?.preComPrice,
    tradeId: tradeAllocationSelector.getTradeId(state, tabId)!,
    applyStatus: tradeAllocationSelector.getApplyStatus(state, tabId)!,
    isReadOnly: tradeAllocationSelector.isReadonly(state, tabId)!,
    isLoading: tradeAllocationSelector.isLoading(state, tabId),
    isin: tradeAllocationSelector.getIsin(state, tabId)!,
    productTradingFormOptions: tradeAllocationSelector.getMonetarySchedule(state, tabId)?.productTradingFormOptions,
    bcsKey: tradeAllocationSelector.getMonetarySchedule(state, tabId)?.bcsKey,
    productId: tradeAllocationSelector.getProductId(state, tabId),
    rfqId: tradeAllocationSelector.getRfqId(state, tabId),
    canApply: hasApplyRights,
  };
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = (dispatch: ThunkDispatch, { tabId }) => ({
  onFightingBackJustificationChanged: fightingBackJustification => {
    dispatch(tradeAllocationFightingBackJustificationChanged(tabId, fightingBackJustification));
  },
  onFightingBackCommentChanged: fightingBackComment => {
    dispatch(tradeAllocationFightingBackCommentChanged(tabId, fightingBackComment));
  },
  onApply: () => {
    logAnalytics.trackEvent('Trade allocation', 'Apply');
    dispatch(tradeAllocationApplyRequestedThunk(tabId));
  },
  onReload: tradeId => {
    dispatch(tradeAllocationFormResetRequested(tabId, tradeId));
  },
  openInSecondaryMarketQuote: (formOption, isin, bcsKey) => {
    dispatch(openInSecondaryMarketFromTradeAllocationThunk(formOption, isin, bcsKey));
  },
  openInAddOn: (productId: string, quoteId: string, isin: string) => {
    dispatch(addonTabCreatedThunk(productId, quoteId, isin));
  },
});

export const connectSidePanel = connect(mapStateToProps, mapDispatchToProps);
